import type {Client} from '@prismicio/client'
import {NotFoundError} from '@prismicio/client'
import type {AiHubGuideDocument, AiHubMcpDocument, AiHubSkillDocument, AiHubToolDocument} from 'prismicio-types'
import {logger} from 'src/common/logger'
import {PrismicLookupError} from 'src/common/utils/prismicErrors'

/**
 * The URL segment the AI Hub's Explore page lives at. Every item document
 * (skill/mcp/tool/guide) resolves one level under this path, and row pages
 * resolve under its `rows/` sub-path — see docs in the AI Hub planning folder
 * ("Routing: reuse the existing leaf-document pattern").
 */
export const AI_HUB_PAGE_UID = 'ai-hub'

export type AiHubItemDocument = AiHubSkillDocument | AiHubMcpDocument | AiHubToolDocument | AiHubGuideDocument

export type AiHubItemFormat = 'skill' | 'mcp' | 'tool' | 'guide'

const AI_HUB_ITEM_TYPES = ['ai_hub_skill', 'ai_hub_mcp', 'ai_hub_tool', 'ai_hub_guide'] as const

type AiHubItemType = (typeof AI_HUB_ITEM_TYPES)[number]

/**
 * All 4 item document type IDs, for `.has()` checks in the link resolver
 * (`prismicio.tsx`) — every item type resolves to the same `/ai-hub/{uid}`
 * shape, so the resolver needs a single set to test against rather than
 * repeating each type name.
 */
export const AI_HUB_ITEM_DOCUMENT_TYPES: ReadonlySet<string> = new Set(AI_HUB_ITEM_TYPES)

const FORMAT_BY_TYPE: Record<AiHubItemType, AiHubItemFormat> = {
  ai_hub_skill: 'skill',
  ai_hub_mcp: 'mcp',
  ai_hub_tool: 'tool',
  ai_hub_guide: 'guide',
}

/**
 * Maps an AI Hub item document to its catalogue format label, used for
 * format filtering and the hardcoded per-type card label (see content model
 * docs: "No `type_label` field").
 */
export function getAiHubItemFormat(doc: AiHubItemDocument): AiHubItemFormat {
  return FORMAT_BY_TYPE[doc.type as AiHubItemType]
}

async function tryGetByUID<T extends Parameters<Client<AiHubItemDocument>['getByUID']>[0]>(
  client: Client<AiHubItemDocument>,
  type: T,
  uid: string,
  locale: string,
) {
  try {
    // `fetchLinks` expands the linked `category` document's `name` — without it, `category.data`
    // is empty and `toDetailItem`'s `categoryName` (used to pick related items) is always null.
    return await client.getByUID(type, uid, {lang: locale, fetchLinks: ['ai_hub_category.name']})
  } catch (error) {
    if (error instanceof NotFoundError) {
      return null
    }

    throw new PrismicLookupError(type, uid, locale, error)
  }
}

/**
 * Resolves a single path segment under `/ai-hub` to whichever of the 4 item
 * types owns that UID. Unlike `findLeafUnderPage` in `pageSlugResolver.ts`,
 * there's no `parent_page` ownership check to perform afterwards — these
 * types have no `parent_page` field at all, since they resolve under the one
 * `ai_hub_page` singleton implicitly (see content model docs).
 *
 * Each item type has its own independent UID field (they're separate Prismic
 * custom types), so Prismic only guarantees uniqueness within one type, not
 * across all 4 — the URL itself (`/ai-hub/{uid}`) has no format segment to
 * disambiguate. If two types share a UID, this picks one deterministically
 * (skill > mcp > tool > guide) and logs a warning, rather than silently
 * serving whichever happened to resolve first.
 */
export async function findAiHubItemByUID(
  client: Client<AiHubItemDocument>,
  uid: string,
  locale: string,
): Promise<AiHubItemDocument | null> {
  const [skill, mcp, tool, guide] = await Promise.all([
    tryGetByUID(client, 'ai_hub_skill', uid, locale),
    tryGetByUID(client, 'ai_hub_mcp', uid, locale),
    tryGetByUID(client, 'ai_hub_tool', uid, locale),
    tryGetByUID(client, 'ai_hub_guide', uid, locale),
  ])

  const matches = [skill, mcp, tool, guide].filter((doc): doc is NonNullable<typeof doc> => doc !== null)

  if (matches.length > 1) {
    logger.warn('AI Hub UID collision across formats', {
      uid,
      locale,
      types: matches.map((doc) => doc.type),
      kept: matches[0].type,
    })
  }

  return matches[0] ?? null
}
